import React from 'react';

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

import { setRoomPosition } from 'store/page';

import { RoomItem } from './RoomItem';
import * as t from './RoomList.types';

export const RoomList = (props: t.Props) => {
  const dispatch = useDispatch();
  const rooms = props.rooms;
  const [draggingItemId, setDraggingItemId] = React.useState<number | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const renderDruggingOverlay = React.useCallback(() => {
    if (!draggingItemId) return null;

    const druggingItem = rooms.find((room) => room.id === draggingItemId);
    if (!druggingItem) return null;

    return createPortal(
      <DragOverlay adjustScale={false}>
        {draggingItemId ? (
          <RoomItem
            isDisabled
            room={druggingItem}
            isSortingDisabled={rooms.length === 1}
            onEdit={() => null}
            onDelete={() => null}
          />
        ) : null}
      </DragOverlay>,
      document.body,
    );
  }, [draggingItemId, rooms]);

  const handleDragEnd = (event: DragEndEvent) => {
    document.body.classList.remove('dragging');
    const { active, over } = event;
    if (!(active && over)) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = rooms.map((e) => e.id).indexOf(Number(active.id));
      const newIndex = rooms.map((e) => e.id).indexOf(Number(over?.id));

      const movedRooms = arrayMove(rooms, oldIndex, newIndex);

      dispatch(setRoomPosition(movedRooms));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }
        document.body.classList.add('dragging');
        setDraggingItemId(Number(active.id));
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        document.body.classList.remove('dragging');
        setDraggingItemId(Number(null));
      }}
    >
      <SortableContext items={rooms} strategy={verticalListSortingStrategy}>
        {rooms.map((room, index) => {
          return (
            <RoomItem
              isDisabled={props.isDisabled}
              key={room.id}
              room={room}
              isSortingDisabled={rooms.length === 1}
              onEdit={() => props.handleEditRoom(index)}
              onDelete={() => props.handleDeleteRoom(index)}
            />
          );
        })}

        {renderDruggingOverlay()}
      </SortableContext>
    </DndContext>
  );
};
